import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import type { PrivacyMode } from "@backstory/protocol";
import { OrgLayout } from "../../components/Shell";
import { Field, PageHeader, Switch } from "../../components/ui";
import { privacyLabel } from "../../lib/format";
import { findHits, maskDocumentForPreview, type PreviewResult } from "../../lib/privacy-preview";

const SAMPLE = `<h2>Patient intake</h2>
<p>Contact: jane.doe@example.com · (555) 123-4567</p>
<p>Card on file: 4111 1111 1111 1111</p>
<label>Name <input value="Jane Doe" autocomplete="off"></label>
<label>Password <input type="password" value="hunter22" autocomplete="off"></label>
<div data-backstory-mask>MRN 00918273 — do not record</div>
<img src="https://example.com/xray.png" alt="x-ray" data-backstory-block>
<p data-backstory-unmask>Public notice text</p>`;

export function PrivacyPreviewPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const [mode, setMode] = useState<PrivacyMode>("strict");
  const [allowUnmask, setAllowUnmask] = useState(false);
  const [source, setSource] = useState<"html" | "url">("html");
  const [html, setHtml] = useState(SAMPLE);
  const [url, setUrl] = useState("");
  const [fetched, setFetched] = useState<string | null>(null);
  const [fetchErr, setFetchErr] = useState<string | null>(null);

  const input = source === "url" ? fetched ?? "" : html;
  const result = useMemo<{ preview: PreviewResult; hits: ReturnType<typeof findHits> } | null>(() => {
    if (!input) return null;
    const doc = new DOMParser().parseFromString(input, "text/html");
    const hits = findHits(doc, mode);
    const preview = maskDocumentForPreview(doc, mode, allowUnmask);
    return { preview, hits };
  }, [input, mode, allowUnmask]);

  const loadUrl = async () => {
    setFetchErr(null);
    setFetched(null);
    try {
      const u = new URL(url);
      if (u.origin !== window.location.origin) {
        setFetchErr("Only same-origin URLs can be loaded here (browser security). Paste the page HTML instead, or run the Privacy Preview from a page where the SDK is installed.");
        return;
      }
      const r = await fetch(u.toString());
      setFetched(await r.text());
    } catch (e) {
      setFetchErr((e as Error).message);
    }
  };

  const s = result?.preview.stats;
  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Privacy preview"
        back={<Link to="/orgs/$org/settings" params={{ org: orgId }} className="btn ghost" aria-label="Back to organization settings">←</Link>}
      />
      <div className="card section">
        <div className="actions-row" style={{ alignItems: "flex-end" }}>
          <Field label="Masking">
            {(p) => (
              <select {...p} className="input" value={mode} onChange={(e) => setMode(e.target.value as PrivacyMode)}>
                <option value="strict">All text and inputs</option>
                <option value="balanced">Inputs only</option>
                <option value="permissive">Marked and sensitive data only</option>
              </select>
            )}
          </Field>
          <div style={{ marginBottom: 16 }}>
            <Switch checked={allowUnmask} onChange={setAllowUnmask} label="Show sections marked to show" />
          </div>
          <div style={{ marginBottom: 16 }} className="actions-row">
            <button className={`btn sm ${source === "html" ? "primary" : ""}`} onClick={() => setSource("html")}>Paste HTML</button>
            <button className={`btn sm ${source === "url" ? "primary" : ""}`} onClick={() => setSource("url")}>Load URL</button>
          </div>
        </div>
        {source === "html" ? (
          <Field label="Page HTML">{(p) => <textarea {...p} className="input" value={html} onChange={(e) => setHtml(e.target.value)} style={{ minHeight: 140 }} />}</Field>
        ) : (
          <div className="actions-row" style={{ alignItems: "flex-end" }}>
            <Field label="URL" hint="Same origin only." error={fetchErr}>{(p) => <input {...p} className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`${window.location.origin}/some-page`} style={{ width: 420 }} />}</Field>
            <button className="btn" style={{ marginBottom: 16 }} onClick={() => void loadUrl()} disabled={!url}>Load</button>
          </div>
        )}
      </div>

      {result && s && (
        <>
          <div className="pill-row" style={{ margin: "16px 0" }} data-testid="preview-stats">
            <span className="badge neutral">{s.textNodes} text nodes</span>
            <span className={`badge ${s.maskedText ? "ok" : "neutral"}`}>{s.maskedText} masked</span>
            <span className="badge neutral">{s.maskedInputs}/{s.inputs} inputs masked</span>
            <span className="badge neutral">{s.blockedImages}/{s.images} images blocked</span>
            {(Object.entries(s.hits) as Array<[string, number]>).filter(([, n]) => n > 0).map(([k, n]) => (
              <span key={k} className="badge warn">{n} {k}</span>
            ))}
            {result.hits.length > 0 && <span className="badge warn">{result.hits.length} email addresses, phone numbers, or similar details are shown</span>}
          </div>
          <div className="preview-grid">
            <div className="preview-pane">
              <header>Original</header>
              <iframe title="Original page" sandbox="" srcDoc={input} />
            </div>
            <div className="preview-pane">
              <header>What Backstory records ({privacyLabel(mode)})</header>
              <div className="doc" data-testid="masked-preview" dangerouslySetInnerHTML={{ __html: result.preview.html }} />
            </div>
          </div>
        </>
      )}
    </OrgLayout>
  );
}
